import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageURL } from 'src/app/pages/pages';
import { UserBasicInfo } from '../../services/user-basic.service';
import { ThemeService } from 'src/app/shared/services/theme.service';

@Component({
    selector: 'app-bottom-navigation',
    templateUrl: './bottom-navigation.component.html',
    styleUrls: ['./bottom-navigation.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BottomNavigationComponent implements OnInit, OnChanges {
  @Input() activeUrl: string;
  @Input() userInfo?: UserBasicInfo;
  @Output() activePage = new EventEmitter<string>();

  PageURL: typeof PageURL = PageURL;
  selectedTheme: string = 'light';
  activeSection: any = {
    markets: false,
    accounts: false,
    payments: false,
    more: false,
    copytrade: false,
    tools: false
  };
  accountsIcon = 'assets/images/footer/accounts.svg';
  paymentsIcon = 'assets/images/footer/payments.svg';
  marketsIcon = 'assets/images/footer/market-watch.svg';
  copytradeIcon = 'assets/icon/menu/wb-copytrading-inactive.svg';
  viewMoreIcon = 'assets/images/footer/view-more.svg';

  constructor(
    public readonly router: Router,
    public readonly activeRoute: ActivatedRoute,
    private readonly translate: TranslateService,
    public readonly popoverController: PopoverController,
    private readonly theme: ThemeService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {}

  ngOnInit() {
    this.theme.getActiveTheme()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(val => {
        this.selectedTheme = val;
        this.refreshNavigationIcons();
        this.cdRef.markForCheck();
      });

    if (this.activeUrl) {
      this.setActiveUrl();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.activeUrl) {
      return;
    }

    if (((!this.activeUrl.includes('/accounts') && !this.activeUrl.includes('/view-more'))) && this.userInfo?.Verified == false) {
      this.userInfo.actions.forEach(action => {
        if (action.id == 'VerifyEmail') {
          this.router.navigate(['/accounts']);
          if (this.activeUrl == '/accounts' || this.activeUrl == '/') return;
          this.emailVerifyPopover(this.translate.instant(action.text), this.userInfo?.Email);
        }
      });
      return;
    } else if (changes['activeUrl'] && this.activeUrl) {
      this.setActiveUrl();
    }
  }

  async emailVerifyPopover(text: string, userEmail: any) {
    const { EmailVerifyPopoverComponent } = await import('../notifications/email-verify-popover/email-verify-popover.component');
    const popover = await this.popoverController.create({
      component: EmailVerifyPopoverComponent,
      componentProps: {
        text: text,
        userEmail: userEmail
      },
      cssClass: 'email-verify-popover'
    });
    return await popover.present();
  }

  setActiveUrl() {
    this.activeSection.markets = this.activeUrl?.includes('instruments') || this.activeUrl?.includes('news') || this.activeUrl?.includes('economic') || this.activeUrl?.includes('analytics') || this.activeUrl?.includes('ideas');
    this.activeSection.accounts = this.activeUrl == '/' || this.activeUrl?.includes('accounts');
    this.activeSection.payments = this.activeUrl?.includes('payments') || this.activeUrl?.includes('deposit-form-page');
    this.activeSection.copytrade = this.activeUrl?.includes('copy-trading');
    this.activeSection.more = this.activeUrl?.includes('view-more') || this.activeUrl?.includes('upload');
    this.activeSection.tools = this.activeUrl?.includes('webinars') || this.activeUrl?.includes('education') || this.activeUrl?.includes('calculator');
    this.refreshNavigationIcons();
  }

  navigateToPage(url: string) {
    if (url !== 'Markets' && url !== 'Tools') {
      this.router.navigate([url]);
      this.activePage.emit(null);
    } else {
      this.activePage.emit(url);
    }
  }

  private refreshNavigationIcons(): void {
    const isLight = this.selectedTheme == 'light';
    const isMarketsActive = this.activeSection.markets;
    const isAccountsActive = this.activeSection.accounts;
    const isPaymentsActive = this.activeSection.payments;
    const isCopytradeActive = this.activeSection.copytrade;
    const isMoreActive = this.activeSection.more;

    this.marketsIcon = isMarketsActive
      ? (isLight ? 'assets/icon/menu/wb-markets-active.svg' : 'assets/images/footer/market-watch-dark-active.svg')
      : (isLight ? 'assets/images/footer/market-watch.svg' : 'assets/images/footer/market-watch-dark.svg');

    this.accountsIcon = isAccountsActive
      ? (isLight ? 'assets/images/footer/accounts-active.svg' : 'assets/images/footer/accounts-dark-active.svg')
      : (isLight ? 'assets/images/footer/accounts.svg' : 'assets/images/footer/accounts-dark.svg');

    this.paymentsIcon = isPaymentsActive
      ? (isLight ? 'assets/images/footer/payments-active.svg' : 'assets/images/footer/payments-dark-active.svg')
      : (isLight ? 'assets/images/footer/payments.svg' : 'assets/images/footer/payments-dark.svg');

    this.copytradeIcon = isCopytradeActive
      ? 'assets/icon/menu/wb-copytrading-active.svg'
      : (isLight ? 'assets/icon/menu/wb-copytrading-inactive.svg' : 'assets/icon/menu/wb-copytrading-inactive-dark.svg');

    this.viewMoreIcon = isMoreActive
      ? 'assets/icon/menu/wb-viewmore-active.svg'
      : (isLight ? 'assets/images/footer/view-more.svg' : 'assets/images/footer/view-more-dark.svg');
  }
}
